/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import {Clickable, Typography} from "@insite/mobius/index";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { screen } from "@monsma/XDL/Helpers";
import { mergeWithCss } from "@monsma/XDL/Utilities";
import { imageHoverEffect } from "@monsma/XDL/Utilities/sharedStyles";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    category: any;
    showImages?: boolean;
    dataTestSelector?: string;
    extendedStyles?: CategoryTileStyles;
    fullImage?: boolean;
    titleVariant?: string;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        customSettings: getSettingsCollection(state).customSettings,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CategoryTileStyles {
    categoryTile?: InjectableCss;
    categoryImageWrapper?: ClickablePresentationProps;
    categoryImage?: InjectableCss;
    categoryTextLink?: LinkPresentationProps;
}

export const categoryTileStyles: CategoryTileStyles = {
    categoryTile: {
        css: css`
            width: 100%;
            text-align: center;
        `,
    },
    categoryImageWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
            border: 1px solid ${getColor("common.ui12")};
            background: ${getColor("common.background")};
            padding: 15px;
            margin-bottom: 15px;
            height: 126px;
            overflow: hidden;

            ${screen("lte", "lg")} {
              height: 160px;
            }
          
            ${screen("lte", "xs")} {
              height: 180px;
            }
          
            &:hover,
            &:focus {
                > div {
                    &:after {
                        opacity: 1;
                    }
                }
            }

            &.full-image {
                padding: 0;
            }
        `,
    },
    categoryImage: {
        css: css`
            width: 100%;

            > img {
                object-fit: cover;
                object-position: center center;
                height: 100%;
            }

            ${imageHoverEffect};
        `,
    },
    categoryTextLink: {
        typographyProps: {
            size: "16px",
            weight: "bolder",
            lineHeight: "22px",
        },
    },
};

const CategoryTile = ({ category, showImages, dataTestSelector, extendedStyles, fullImage, customSettings, titleVariant }: Props) => {
    const [styles] = useState(() => mergeWithCss(categoryTileStyles, extendedStyles));
    const categoryTitleLink = (
        <Link
            href={category.path}
            data-test-selector={`${dataTestSelector}${category.id}`}
            {...styles.categoryTextLink}
        >
            {category.shortDescription}
        </Link>
    );
    return (
        <StyledWrapper {...styles.categoryTile}>
            {showImages && (
                <Clickable
                    href={category.path}
                    {...styles.categoryImageWrapper}
                    className={`${fullImage && "full-image"}`}
                >
                    <LazyImage
                        src={category.smallImagePath || (customSettings as any)?.defaultMediumCategoryImagePath}
                        altText={category.imageAltText || category.shortDescription}
                        {...styles.categoryImage}
                    />
                </Clickable>
            )}
            {titleVariant && (
                <Typography variant={titleVariant as any} css={css`margin-bottom: 0;font-weight: unset;line-height: 0;`}>
                    {categoryTitleLink}
                </Typography>
            )}
            {!titleVariant && categoryTitleLink}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(CategoryTile);
